import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  PPPC_SERVICE_GROUPS,
  PppcChoicesMap,
  getPppcServiceDef,
  getPppcServiceGroupLabel,
  isCustomPppcServiceKey,
} from "@/types/pppc";
import { PrivacyServiceRow } from "./PrivacyServiceRow";
import { AddPermissionMenu } from "./AddPermissionMenu";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertTriangle, ExternalLink, Info, ShieldOff } from "lucide-react";
import {
  PppcValidationIssue,
  validatePppcChoices,
} from "@/services/pppcValidation";

export interface PrivacyPermissionsFormValue {
  codeRequirement: string;
  choices: PppcChoicesMap;
}

interface PrivacyPermissionsFormProps {
  bundleId: string;
  value: PrivacyPermissionsFormValue;
  onChange: (next: PrivacyPermissionsFormValue) => void;
  /** Caller controls whether to show validation errors (e.g. after Save click). */
  errors?: { codeRequirement?: string; serviceIssues?: PppcValidationIssue[] };
  scrollServices?: boolean;
  /**
   * When true, all inputs become read-only / non-interactive and a banner
   * explains why. Used by the wizard when the selected app's platform is no
   * longer macOS but the user hasn't navigated off the PPPC step yet.
   */
  disabled?: boolean;
  /** Optional explanation rendered in the disabled-state banner. */
  disabledReason?: string;
}

// (Custom-key detection now lives in `isCustomPppcServiceKey` so the row,
// summary card, and form all classify entries identically.)

/** Order configured services by their group order in PPPC_SERVICE_GROUPS, then alphabetical. */
const sortConfiguredKeys = (keys: string[]): string[] => {
  const groupOrder = new Map<string, number>();
  PPPC_SERVICE_GROUPS.forEach((g, i) => groupOrder.set(g.groupLabel, i));
  return [...keys].sort((a, b) => {
    const ga = groupOrder.get(getPppcServiceGroupLabel(a)) ?? 999;
    const gb = groupOrder.get(getPppcServiceGroupLabel(b)) ?? 999;
    if (ga !== gb) return ga - gb;
    return getPppcServiceDef(a).label.localeCompare(getPppcServiceDef(b).label);
  });
};

export const PrivacyPermissionsForm = ({
  bundleId,
  value,
  onChange,
  errors,
  scrollServices = true,
  disabled = false,
  disabledReason,
}: PrivacyPermissionsFormProps) => {
  const setCodeRequirement = (codeRequirement: string) => {
    if (disabled) return;
    onChange({ ...value, codeRequirement });
  };

  const setChoice = (key: string, choice: "allow" | "deny") => {
    if (disabled) return;
    const nextChoices: PppcChoicesMap = { ...value.choices, [key]: choice };
    onChange({ ...value, choices: nextChoices });
  };

  const removeService = (key: string) => {
    if (disabled) return;
    const nextChoices: PppcChoicesMap = { ...value.choices };
    delete nextChoices[key];
    onChange({ ...value, choices: nextChoices });
  };

  const addService = (key: string) => {
    if (disabled) return;
    const def = getPppcServiceDef(key);
    // Default deny-only services to "deny", everything else to "allow".
    const initial: "allow" | "deny" = def.denyOnly ? "deny" : "allow";
    const nextChoices: PppcChoicesMap = { ...value.choices, [key]: initial };
    onChange({ ...value, choices: nextChoices });
  };

  // Configured = present in choices with allow/deny.
  const configuredKeys = sortConfiguredKeys(
    Object.entries(value.choices)
      .filter(([, v]) => v === "allow" || v === "deny")
      .map(([k]) => k),
  );

  const servicesContent =
    configuredKeys.length === 0 ? (
      <div className="rounded-md border border-dashed py-10 px-6 text-center">
        <div className="mx-auto h-10 w-10 rounded-full bg-muted flex items-center justify-center mb-3">
          <ShieldOff className="h-5 w-5 text-muted-foreground" />
        </div>
        <p className="text-sm font-medium">No managed permissions yet</p>
        <p className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
          macOS will prompt the user normally for any service you don't add here.
          Click <strong>Add Permission</strong> to pre-approve or deny specific
          services for this app.
        </p>
        <div className="mt-4 flex justify-center">
          <AddPermissionMenu
            existingKeys={configuredKeys}
            onAdd={addService}
            disabled={disabled}
          />
        </div>
      </div>
    ) : (
      <div className="rounded-md border">
        {configuredKeys.map((key) => {
          const def = getPppcServiceDef(key);
          const choice = value.choices[key] as "allow" | "deny";
          return (
            <PrivacyServiceRow
              key={key}
              service={def}
              value={choice}
              onChange={(next) => setChoice(key, next)}
              onRemove={() => removeService(key)}
              disabled={disabled}
              isCustom={isCustomPppcServiceKey(key)}
            />
          );
        })}
      </div>
    );

  return (
    <div className={`space-y-6 ${disabled ? "opacity-75" : ""}`} aria-disabled={disabled || undefined}>
      {disabled && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            {disabledReason ??
              "Privacy Permissions (PPPC) only apply to macOS apps. These inputs are disabled because the selected app is not macOS."}
          </AlertDescription>
        </Alert>
      )}

      {/* Section A — App Identity */}
      <section className="space-y-4">
        <div>
          <h3 className="text-sm font-semibold">App Identity</h3>
          <p className="text-xs text-muted-foreground mt-1">
            The app is identified by its bundle ID. Provide the codesign requirement
            so macOS can verify the binary matches.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-muted-foreground">Bundle ID</Label>
            <Input value={bundleId} readOnly className="mt-1 font-mono text-sm bg-muted" />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Identifier Type</Label>
            <Input value="bundleID" readOnly className="mt-1 font-mono text-sm bg-muted" />
          </div>
        </div>

        <div>
          <Label htmlFor="pppc-code-req" className="text-sm">
            Code Requirement <span className="text-destructive">*</span>
          </Label>
          <Input
            id="pppc-code-req"
            value={value.codeRequirement}
            onChange={(e) => setCodeRequirement(e.target.value)}
            placeholder={`identifier "${bundleId}" and anchor apple generic and ...`}
            className="mt-1 font-mono text-xs"
            aria-invalid={!!errors?.codeRequirement}
            disabled={disabled}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Run{" "}
            <code className="px-1 py-0.5 rounded bg-muted font-mono text-[11px]">
              codesign -dr - /Applications/YourApp.app
            </code>{" "}
            in Terminal and paste the value after{" "}
            <code className="font-mono">designated =&gt;</code>.{" "}
            <a
              href="https://developer.apple.com/documentation/devicemanagement/privacypreferencespolicycontrol"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline inline-flex items-center gap-1"
            >
              Apple docs <ExternalLink className="h-3 w-3" />
            </a>
          </p>
          {errors?.codeRequirement && (
            <p className="text-xs text-destructive mt-1">{errors.codeRequirement}</p>
          )}
        </div>
      </section>

      {/* Section B — Privacy Services */}
      <section className="space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-sm font-semibold">Privacy Services</h3>
            <p className="text-xs text-muted-foreground mt-1">
              Add only the services you want to manage. Anything not listed is
              left to the user to grant or deny.
            </p>
          </div>
          {configuredKeys.length > 0 && (
            <AddPermissionMenu
              existingKeys={configuredKeys}
              onAdd={addService}
              disabled={disabled}
            />
          )}
        </div>

        {errors?.serviceIssues && errors.serviceIssues.length > 0 && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>
              {errors.serviceIssues.length === 1
                ? "Invalid custom TCC service"
                : `${errors.serviceIssues.length} invalid custom TCC services`}
            </AlertTitle>
            <AlertDescription>
              <ul className="mt-1 space-y-1 list-disc pl-5">
                {errors.serviceIssues.map((issue) => (
                  <li key={issue.key} className="text-xs">
                    <code className="font-mono">{issue.key}</code> — {issue.message}
                  </li>
                ))}
              </ul>
              <p className="text-xs mt-2">
                Remove or edit these entries before saving.
              </p>
            </AlertDescription>
          </Alert>
        )}

        {scrollServices && configuredKeys.length > 0 ? (
          <ScrollArea className="h-[420px] pr-4">{servicesContent}</ScrollArea>
        ) : (
          servicesContent
        )}
      </section>
    </div>
  );
};

export const emptyPppcFormValue = (): PrivacyPermissionsFormValue => ({
  codeRequirement: "",
  choices: {},
});

export const validatePppcForm = (
  value: PrivacyPermissionsFormValue,
): { codeRequirement?: string; serviceIssues?: PppcValidationIssue[] } => {
  const errors: {
    codeRequirement?: string;
    serviceIssues?: PppcValidationIssue[];
  } = {};
  const hasAnyChoice = Object.values(value.choices).some(
    (c) => c === "allow" || c === "deny",
  );
  if (hasAnyChoice && value.codeRequirement.trim().length < 10) {
    errors.codeRequirement =
      "Code Requirement is required and must be at least 10 characters.";
  }
  const serviceIssues = validatePppcChoices(value.choices);
  if (serviceIssues.length > 0) errors.serviceIssues = serviceIssues;
  return errors;
};
